// ProfileStore.ts
import { ref } from 'vue';
import { defineStore } from 'pinia';

import { useProfile } from '@/views/profile/hooks/useProfile';
import type { SimpleProfile } from '@/views/profile/profileTypes';
import { eventBus } from '@/configs/eventBus';

export const useProfileStore = defineStore(
  'profile',
  () => {
    const userProfile = ref<SimpleProfile | undefined>(undefined);

    const isFetchingProfile = ref(false);
    const lastFetchErrorStatus = ref<number | null>(null);
    const isProfileResolved = ref(false);

    // Single-flight: avoid parallel fetches
    let inFlight: Promise<SimpleProfile | undefined> | null = null;

    const { fetchProfile } = useProfile();

    async function fetchUserProfile(): Promise<SimpleProfile | undefined> {
      if (inFlight) return inFlight;

      isFetchingProfile.value = true;
      lastFetchErrorStatus.value = null;

      inFlight = (async () => {
        try {
          const profile = await fetchProfile();
          userProfile.value = profile;
          lastFetchErrorStatus.value = null;
          return profile;
        } catch (err: any) {
          // Try to extract HTTP status if available
          const status =
            err?.response?.status ??
            err?.status ??
            (typeof err?.message === 'string' && err.message.includes('403') ? 403 : null);

          lastFetchErrorStatus.value = typeof status === 'number' ? status : null;
          return undefined;
        } finally {
          isFetchingProfile.value = false;
          inFlight = null;
          isProfileResolved.value = true;
        }
      })();

      return inFlight;
    }

    function clearUserProfile() {
      userProfile.value = undefined;
      lastFetchErrorStatus.value = null;
      isFetchingProfile.value = false;
      inFlight = null;
      isProfileResolved.value = false;
    }

    const onTokenUpdated = () => {
      void fetchUserProfile();
    };
    const onTokenCleared = () => {
      clearUserProfile();
    };

    // Use on/off without mount (it's a store)
    eventBus.on('tokenUpdated', onTokenUpdated);
    eventBus.on('tokenCleared', onTokenCleared);

    // optional
    // function dispose() {
    //   eventBus.off('tokenUpdated', onTokenUpdated);
    //   eventBus.off('tokenCleared', onTokenCleared);
    // }

    return {
      userProfile,
      isFetchingProfile,
      lastFetchErrorStatus,
      fetchUserProfile,
      clearUserProfile,
      isProfileResolved,
      // dispose,
    };
  },
  { persist: true },
);
